import React from 'react';

import styled from '@emotion/styled';
import { DateTime } from 'luxon';

import { Picture } from '@shared/components/helpers';
import { ImgixBG, ImgixFit } from '@shared/utils/imgix';

import { shiftEntryStyle } from './shift_entry_style';
import { IClockin, IImage, IShift } from './types';
import { clockinDistanceMiles } from './utilities';

export const formatDateTz = (timestamp: string, timezone: string, format: string, ignoreAbbr = false) => {
  const dateTime = DateTime.fromISO(timestamp, { zone: timezone });
  const formatted = dateTime.toFormat(format);
  if (ignoreAbbr) {
    return formatted;
  }
  const abbr = dateTime.offsetNameShort ?? dateTime.toFormat('ZZZZ');
  return `${formatted} ${abbr}`;
};

const formatIntervalMinutes = (from: string, till: string) => {
  const minutes = DateTime.fromISO(till).diff(DateTime.fromISO(from), 'minutes').minutes;
  return `${Math.round(minutes)} mins`;
};

const formatShiftDuration = (durationSeconds: number | undefined | null) => {
  if (!Number.isInteger(durationSeconds)) {
    return '';
  }
  const hours = Math.floor(durationSeconds! / 3600);
  const minutes = Math.floor(durationSeconds! / 60) % 60;
  const paddedMinutes = (minutes < 10 ? '0' : '') + minutes;
  return `${hours}:${paddedMinutes}`;
};

const warnForNoWorkBreak = (shift: IShift) => !shift.work_breaks.length && shift.duration > 60 * 60 * 4;

const distanceBetweenTimestamps = (from: string, till: string, tz: string, units: 'minutes' | 'hours') => {
  return DateTime.fromISO(from, { zone: tz }).diff(DateTime.fromISO(till, { zone: tz }), units)[units];
};

const warnForWorkBreakTiming = (shift: IShift, workBreak: { started?: string; ended?: string }) => {
  if (!workBreak.started || !workBreak.ended) {
    return false;
  }
  return (
    distanceBetweenTimestamps(workBreak.ended, workBreak.started, shift.user.tz, 'minutes') < 30 ||
    (shift.started && distanceBetweenTimestamps(workBreak.started, shift.started.timestamp, shift.user.tz, 'hours') > 5)
  );
};

interface ClockinEntryProps {
  clockin: IClockin;
  align: 'left' | 'right';
  timezone: string;
  onLightbox(image: IClockin['image']): void;
  onMap(clockin: IClockin): void;
}

const ClockinEntryButton = styled.button`
  cursor: pointer;
  outline: none;
  border: none;
  margin: 0;
  padding: 0;
`;

const ClockinEntry: React.FC<ClockinEntryProps> = ({ clockin, align, timezone, onLightbox, onMap }) => {
  const alignmentClass = align === 'left' ? 'shifts-entry-lalign' : 'shifts-entry-ralign';

  return (
    <div className={alignmentClass}>
      <ClockinEntryButton
        className="shifts-entry-thumb"
        type="button"
        onClick={() => onLightbox(clockin.image)}
        aria-label="View clock-in photo"
      >
        <Picture
          source={clockin.image?.state === 'uploaded' ? clockin.image.source : undefined}
          width={80}
          height={80}
          fit={ImgixFit.Crop}
          background={ImgixBG.White}
          className="shifts-entry-image"
          alt="Clock-in photo"
        />
      </ClockinEntryButton>
      <ClockinEntryButton
        type="button"
        className="shifts-entry-notes"
        onClick={() => onMap(clockin)}
        aria-label={`View clock-in location on map, ${formatDateTz(clockin.timestamp, timezone, 'MMM d, yyyy h:mm a')}`}
      >
        <span
          className={clockinDistanceMiles(clockin.distance) > 0.1 ? 'shifts-entry-warn' : undefined}
          title={formatDateTz(clockin.timestamp, timezone, 'MMM d, yyyy h:mm a')}
        >
          {formatDateTz(clockin.timestamp, timezone, 'h:mm a')}
          <br />
          {formatDateTz(clockin.timestamp, timezone, 'MMM d, yyyy', true)}
        </span>
      </ClockinEntryButton>
    </div>
  );
};

export const ShiftEntryTimeline: React.FC<{
  shift: IShift;
  onLightbox(image: IClockin['image']): void;
  onMap(clockin: IClockin): void;
}> = ({ shift, onLightbox, onMap }) => (
  <div className="shifts">
    {shift.started && (
      <div
        className="shifts-entry shifts-entry-soft"
        style={shiftEntryStyle(shift.started.timestamp, shift.ended?.timestamp, shift.user.tz)}
      >
        {shift.started && (
          <ClockinEntry
            clockin={shift.started}
            align="left"
            timezone={shift.user.tz}
            onLightbox={onLightbox}
            onMap={onMap}
          />
        )}
        {shift.ended && (
          <ClockinEntry
            clockin={shift.ended}
            align="right"
            timezone={shift.user.tz}
            onLightbox={onLightbox}
            onMap={onMap}
          />
        )}
        {warnForNoWorkBreak(shift) && (
          <div className="shifts-entry-centered">
            <span className="shifts-entry-warn">no lunch</span>
          </div>
        )}
        {shift.work_breaks
          .filter((workBreak) => !workBreak.started || !workBreak.ended)
          .map((workBreak) => (
            <div key={workBreak.uuid} className="shifts-entry-centered">
              <span className="shifts-entry-warn">incomplete work break</span>
            </div>
          ))}
      </div>
    )}
    {shift.work_breaks
      .filter((workBreak) => workBreak.started && workBreak.ended)
      .map((workBreak) => (
        <div
          key={workBreak.uuid}
          className="shifts-entry shifts-entry-bold"
          style={shiftEntryStyle(
            workBreak.started!,
            workBreak.ended!,
            shift.user.tz,
            shift.started?.timestamp ?? workBreak.started!,
          )}
        >
          <div className="hover">
            <div className="shifts-entry-centered">
              <span className={warnForWorkBreakTiming(shift, workBreak) ? 'shifts-entry-warn' : undefined}>
                {formatIntervalMinutes(workBreak.started!, workBreak.ended!)}
              </span>
            </div>
          </div>
        </div>
      ))}
  </div>
);

export const ShiftDurationDisplay: React.FC<{ duration: number }> = ({ duration }) => (
  <span className={duration > 60 * 60 * 8 || !duration ? 'shifts-entry-warn' : undefined}>
    {formatShiftDuration(duration)}
  </span>
);
